import {
    FC,
    getProp,
    ExtendProps,
    GraphicsProps,
    $Graphics,
    CanBeSignal,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {FullScreenFaderV2, FullScreenFaderV2Options} from '@gongxigames/framework/core/fragments/display/SafeZone/FullScreenFaderV2';
import {Store} from '@gongxigames/framework/core/fragments/store';
import {slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {SafeZone} from '@gongxigames/framework/core/fragments/display/SafeZone/SafeZone';
import type {UpdateManager} from '@gongxigames/framework/core';

type ContainerPropsForFaderType = Omit<GraphicsProps, 'width' | 'height' | 'x' | 'y' | 'position' | 'scale'>;

type BaseFaderProps = {
    opacity: CanBeSignal<number>;
    show?: CanBeSignal<boolean>;
    duration?: CanBeSignal<number>;
    updater?: CanBeSignal<UpdateManager>;
    safeZone?: CanBeSignal<SafeZone>;
    color?: CanBeSignal<number>;
};

export type FullScreenFaderProps = BaseFaderProps & ExtendProps<{}, ContainerPropsForFaderType, FullScreenFaderV2>;

function createInstance(props: BaseFaderProps): FullScreenFaderV2 {
    const safeZone = getProp(props.safeZone) ?? slotStore.stage_safeZoneState_game.value;
    const color = getProp(props.color);
    const opacity = getProp(props.opacity);

    const options: FullScreenFaderV2Options = {
        safeZone,
        opacity,
        color,
    };

    return new FullScreenFaderV2(options);
}

export const $FullScreenFader: FC<FullScreenFaderProps> = (props) => {
    const instance = props.instance ??= createInstance(props);
    const offResize = strictUiEmitter.on('CORE.screen.resize', () => {
        const safeZone = getProp(props.safeZone) ?? slotStore.stage_safeZoneState_game.value;

        instance.resize(safeZone);
    });

    const offAnimation = Store.effect(() => {
        const updater = getProp(props.updater) ?? slotStore.updateManager.value!;
        const duration = getProp(props.duration) ?? 200;
        const show = getProp(props.show) ?? true;
        const alpha = show ? 1 : 0;

        if (duration) {
            updater
                .addTween(instance)
                .to({alpha}, duration)
                .start();
        } else {
            instance.alpha = alpha;
        }
    });

    instance.on('destroyed', offResize);
    instance.on('destroyed', offAnimation);

    return $Graphics(props as GraphicsProps);
};